import { Toolbar, Tabs, Tab, Box, Badge, Container } from '@mui/material';
import { useRef, useState } from 'react';
import { ProjectComponentProps } from './Project';
import { PurchaseOrders } from './PurchaseOrders';
import {
  ProjectContext,
  ProjectProvider,
  useProjectContext,
} from 'contexts/project.context';
import { ProjectHeader } from './project/ProjectHeader';
import { ProjectPlan } from './project/ProjectPlan';
import { ProjectTabDrawer } from 'drawers/ProjectTabDrawer';
import { Contact, Project, Comment, Relationship } from 'store/api/models';
import { AlertDialog, selectUser } from '@centerpiece/components';
import { useSelector } from 'react-redux';
import { useGetCommentsQuery, useGetContactQuery } from 'store/api';
import { ProjectInfoTab } from 'drawers/tabs/ProjectInfoTab';
import { ProjectFilesTab } from 'drawers/tabs/ProjectFilesTab';
import { ProjectTeamTab } from 'drawers/tabs/ProjectTeamTab';
import { ProjectCommentsTab } from 'drawers/tabs/ProjectCommentsTab';
import { ProjectBillingsTab } from 'drawers/tabs/ProjectBillingsTab';

export interface TabbedProjectPageProps {
  initialTab?: string;
}

export const TabbedProjectPage = ({
  initialTab,
  ...project_props
}: ProjectComponentProps & TabbedProjectPageProps) => {
  const {
    id,
    template,
    disabled,
    preview,
    header,
    onBack,
    onLogoClick,
    exampleProject,
  } = project_props;
  const [value, setValue] = useState<string>(initialTab || 'schedule');
  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };
  const [openProjectInfo, setOpenProjectInfo] = useState<string>();
  const [openSpeedDial, setOpenSpeedDial] = useState<boolean>(false);
  const itemsRef = useRef({});
  const { contact: userContact } = useSelector(selectUser);
  const { data: contactRecord = {} as Contact, isLoading } = useGetContactQuery(
    { id: userContact, params: { nested: 'all' } },
    { skip: !userContact }
  );

  return (
    <ProjectProvider
      id={id}
      template={template}
      disabled={disabled}
      itemsRef={itemsRef}
      preview={preview}
    >
      <ProjectContext.Consumer>
        {({
          id,
          project,
          template,
          refetchProject,
          role_relationships,
          currentStage,
          comments,
          alertDetails,
          setAlertDetails,
        }) => {
          const numChats = comments?.filter(
            (comment: Comment) =>
              comment?.contacts?.filter(
                (contact: any) =>
                  contact.status === 'New' && contact.contact === userContact
              )?.length > 0
          )?.length;
          const numRolesMissing =
            role_relationships.filter(
              (r: Relationship) =>
                ((!r.contacts || r.contacts?.length === 0) &&
                  r.stages?.filter(s => s.id === currentStage?.id)?.length) ||
                0 > 0
            )?.length || 0;
          return (
            <div
              style={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div>
                <ProjectHeader
                  setOpenProjectInfo={setOpenProjectInfo}
                  onBack={onBack}
                  onLogoClick={onLogoClick}
                  setOpenSpeedDial={setOpenSpeedDial}
                  append={
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Tabs
                        value={value}
                        variant={'scrollable'}
                        onChange={(event, value) => handleChange(event, value)}
                        sx={{
                          width: '100%',
                          '& .MuiTabs-flexContainer': {
                            pl: 0,
                          },
                        }}
                      >
                        <Tab
                          key={'schedule'}
                          label={'Schedule'}
                          value={'schedule'}
                        />
                        <Tab
                          key={'info'}
                          label={'Information'}
                          value={'info'}
                        />
                        <Tab
                          key={'team'}
                          label={
                            <Badge
                              badgeContent={numRolesMissing}
                              color="warning"
                            >
                              Team
                            </Badge>
                          }
                          value={'team'}
                        />
                        <Tab key={'files'} label={'Files'} value={'files'} />
                        <Tab
                          key={'comments'}
                          label={
                            <Badge badgeContent={numChats} color="secondary">
                              Comments
                            </Badge>
                          }
                          value={'comments'}
                        />
                        <Tab
                          key={'billings'}
                          label={'Billings'}
                          value={'billings'}
                        />
                        {/* <Tab
                          key={'site_notes'}
                          label={'Site Notes (coming soon!)'}
                          value={'site_notes'}
                        />
                        <Tab
                          key={'purchase_orders'}
                          label={
                            contactRecord?.enterprise?.form?.aq
                              ? 'Orders'
                              : 'Orders (coming soon!)'
                          }
                          value={'purchase_orders'}
                        /> */}
                      </Tabs>
                    </Box>
                  }
                />
              </div>
              {openProjectInfo && (
                <ProjectTabDrawer
                  template={template}
                  exampleProject={exampleProject?.project as Project}
                  open={!!openProjectInfo}
                  initialTab={openProjectInfo || ''}
                  id={id}
                  archived={project.archived}
                  onClose={() => {
                    setOpenProjectInfo(undefined);
                  }}
                  afterSave={() => refetchProject()}
                />
              )}
              <div
                style={{
                  flexGrow: 1,
                  overflowX: 'hidden',
                  overflowY: 'scroll',
                  paddingBottom: 1,
                }}
              >
                {value === 'schedule' && (
                  <ProjectPlan
                    {...project_props}
                    afterSave={() => refetchProject()}
                  />
                )}
                {value === 'info' && (
                  <ProjectInfoTab
                    {...project_props}
                    afterSave={() => refetchProject()}
                  />
                )}
                {value === 'team' && (
                  <ProjectTeamTab
                    {...project_props}
                    afterSave={() => refetchProject()}
                  />
                )}
                {value === 'files' && <ProjectFilesTab {...project_props} />}
                {value === 'comments' && (
                  <ProjectCommentsTab
                    {...project_props}
                    afterSave={() => refetchProject()}
                  />
                )}
                {value === 'billings' && (
                  <ProjectBillingsTab
                    {...project_props}
                    afterSave={() => refetchProject()}
                  />
                )}
                {value === 'purchase_orders' &&
                  false &&
                  contactRecord?.enterprise?.form?.aq && <PurchaseOrders />}
                <AlertDialog
                  {...(alertDetails || {})}
                  onClose={() => {
                    setAlertDetails({ open: false });
                  }}
                />
              </div>
            </div>
          );
        }}
      </ProjectContext.Consumer>
    </ProjectProvider>
  );
};
